import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import AppLayout from '../../components/AppLayout';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { S, Icon } from '../regureStyles';
import { getLoggedinUserAction } from '../../redux/actions/userActions';

// Must match AccountSettingsScreen's LS_KEY — Navigation reads this
// key when resolving language on mount / user hydrate. Writing to a
// different key made the language revert immediately.
const LOCALE_LS_KEY = 'regure_locale_settings';

const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
];

const MyProfileScreen = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const userLogin = useSelector(state => state.userLogin);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editing, setEditing] = useState(false);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Portal users have no sidebar access to Account Settings (portal
  // sidebar is scoped to my-*), so language changes here. T-11, U-13.
  //
  // First attempt (shipped in 4b4aa3c2) only did i18n.changeLanguage +
  // localStorage.setItem('language', ...). That reverted immediately
  // because Navigation reads the SAVED locale from
  // `regure_locale_settings.language` (not 'language'), and the
  // language-resolution useEffect ranked userPreference and
  // tenantConfig above whatever `savedLanguage` yielded. So on the
  // next render tick i18n snapped back to whichever of those was set.
  //
  // Correct pattern mirrors AccountSettingsScreen.handleSaveLocale:
  //   1. i18n.changeLanguage — updates in-memory locale
  //   2. localStorage['regure_locale_settings'].language — persists
  //   3. PATCH /me/my-profile with {preferences: {language}} — makes
  //      user.preferences.language authoritative on next hydrate
  //   4. dispatch(getLoggedinUserAction()) — refresh Redux so
  //      Navigation's useEffect sees the new preference and doesn't
  //      overwrite our choice
  const handleLanguageChange = async (code) => {
    if (!code || code === i18n.language) return;
    i18n.changeLanguage(code);
    try {
      const prior = localStorage.getItem(LOCALE_LS_KEY);
      const parsed = prior ? JSON.parse(prior) : {};
      parsed.language = code;
      localStorage.setItem(LOCALE_LS_KEY, JSON.stringify(parsed));
    } catch {}
    try {
      await Axios.patch(
        `${apiUrl}/me/my-profile`,
        { preferences: { language: code } },
        { withCredentials: true },
      );
      dispatch(getLoggedinUserAction());
    } catch {
      /* non-fatal — local switch already took effect */
    }
  };

  // Read `data.user` first — that is the shape the userLogin slice actually
  // carries — with `data.data` as a fallback for the older getAll-factory
  // convention. Reading only `data.data` returned undefined, so `role`
  // silently fell back to 'policyholder' and every role-gated block on this
  // screen (broker subtitle, broker FINMA registration fields) was dead.
  const user = userLogin?.userInfo?.data?.user || userLogin?.userInfo?.data?.data || {};
  const role = user?.role || 'policyholder';

  useEffect(() => {
    if (userLogin?.userInfo?.status !== 'success') return;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(`${apiUrl}/me/my-profile`, { withCredentials: true });
        setProfile(response.data?.data || {});
      } catch (err) {
        setError(err.response?.data?.message || t('screens.portal.failedLoadProfile'));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userLogin, apiUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      await Axios.patch(`${apiUrl}/me/my-profile`, profile, { withCredentials: true });
      setSuccess(t('screens.portal.profileUpdated'));
      setEditing(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || t('screens.portal.failedUpdateProfile'));
    }
  };

  if (loading) return (
    <AppLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularLoader />
      </div>
    </AppLayout>
  );

  const pageTitle = t('screens.portal.yourProfile');
  const pageSubtitle = role === 'broker' ? t('screens.portal.profileSubtitleBroker') : t('screens.portal.profileSubtitlePolicyholder');

  return (
    <AppLayout>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: `linear-gradient(135deg, ${S.bg} 0%, #f8fafc 100%)`,
        padding: '32px 40px',
        minHeight: '100%'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <span style={{ color: S.burgundy, fontWeight: 700 }}>{t('screens.portal.breadcrumbPortal')}</span> / {t('screens.portal.breadcrumbMyProfile')}
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, letterSpacing: '-0.5px' }}>
              {pageTitle}
            </div>
            <div style={{ fontSize: 14, color: S.textMuted, marginTop: 6 }}>
              {pageSubtitle}
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(220, 38, 38, 0.15)'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.08)',
            color: '#10b981',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(16, 185, 129, 0.15)'
          }}>
            {success}
          </div>
        )}

        {/* Profile Form Card */}
        <div style={{
          background: '#fff',
          border: `1px solid ${S.borderLight}`,
          borderRadius: 14,
          padding: 32,
          maxWidth: 700,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Name */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                {t('screens.portal.profileName')}
              </div>
              {editing ? (
                <input
                  type="text"
                  name="name"
                  value={profile.name || ''}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    fontWeight: 500,
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = S.burgundy}
                  onBlur={e => e.target.style.borderColor = S.borderLight}
                />
              ) : (
                <div style={{ fontSize: 15, color: S.textPrimary, fontWeight: 600 }}>
                  {profile.name || '—'}
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                {t('screens.portal.profileEmail')}
              </div>
              {editing ? (
                <input
                  type="email"
                  name="email"
                  value={profile.email || ''}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    fontWeight: 500,
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = S.burgundy}
                  onBlur={e => e.target.style.borderColor = S.borderLight}
                />
              ) : (
                <div style={{ fontSize: 15, color: S.textPrimary, fontWeight: 600 }}>
                  {profile.email || '—'}
                </div>
              )}
            </div>

            {/* Phone */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                {t('screens.portal.profilePhone')}
              </div>
              {editing ? (
                <input
                  type="tel"
                  name="phone"
                  value={profile.phone || ''}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    fontWeight: 500,
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = S.burgundy}
                  onBlur={e => e.target.style.borderColor = S.borderLight}
                />
              ) : (
                <div style={{ fontSize: 15, color: S.textPrimary, fontWeight: 600 }}>
                  {profile.phone || '—'}
                </div>
              )}
            </div>

            {/* Address */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                {t('screens.portal.profileAddress')}
              </div>
              {editing ? (
                <input
                  type="text"
                  name="address"
                  value={profile.address || ''}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${S.borderLight}`,
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    fontWeight: 500,
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={e => e.target.style.borderColor = S.burgundy}
                  onBlur={e => e.target.style.borderColor = S.borderLight}
                />
              ) : (
                <div style={{ fontSize: 15, color: S.textPrimary, fontWeight: 600 }}>
                  {profile.address || '—'}
                </div>
              )}
            </div>

            {/* Broker registration (FINMA). The broker maintains this
                themselves — it prints on the proposals they broker as
                "FINMA n° <number>". Shown only to brokers; blank prints
                nothing on the document. */}
            {role === 'broker' && (
              <>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                    {t('screens.portal.profileRegistrationAuthority')}
                  </div>
                  {editing ? (
                    <input
                      type="text"
                      name="registrationAuthority"
                      value={profile.registrationAuthority || ''}
                      onChange={handleChange}
                      placeholder="FINMA"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: `1px solid ${S.borderLight}`,
                        borderRadius: 8,
                        fontSize: 14,
                        outline: 'none',
                        fontWeight: 500,
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={e => e.target.style.borderColor = S.burgundy}
                      onBlur={e => e.target.style.borderColor = S.borderLight}
                    />
                  ) : (
                    <div style={{ fontSize: 15, color: S.textPrimary, fontWeight: 600 }}>
                      {profile.registrationAuthority || '—'}
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                    {t('screens.portal.profileRegistrationNumber')}
                  </div>
                  {editing ? (
                    <input
                      type="text"
                      name="registrationNumber"
                      value={profile.registrationNumber || ''}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        border: `1px solid ${S.borderLight}`,
                        borderRadius: 8,
                        fontSize: 14,
                        outline: 'none',
                        fontWeight: 500,
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={e => e.target.style.borderColor = S.burgundy}
                      onBlur={e => e.target.style.borderColor = S.borderLight}
                    />
                  ) : (
                    <div style={{ fontSize: 15, color: S.textPrimary, fontWeight: 600 }}>
                      {profile.registrationNumber || '—'}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Language — portal users have no other route to change locale */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                {t('screens.portal.language')}
              </div>
              <select
                value={i18n.language && i18n.language.slice(0, 2)}
                onChange={e => handleLanguageChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  fontWeight: 500,
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {SUPPORTED_LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ marginTop: 40, display: 'flex', gap: 12 }}>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                style={{
                  background: S.burgundy,
                  color: '#fff',
                  border: 'none',
                  padding: '10px 22px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#5c001a';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(128, 0, 32, 0.25)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = S.burgundy;
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Icon name="edit-2" color="#fff" size={14} />
                {t('screens.portal.editProfile')}
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  style={{
                    background: S.burgundy,
                    color: '#fff',
                    border: 'none',
                    padding: '10px 22px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#5c001a';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(128, 0, 32, 0.25)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = S.burgundy;
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Icon name="check" color="#fff" size={14} />
                  {t('screens.portal.saveChanges')}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  style={{
                    background: '#fff',
                    color: S.textSecondary,
                    border: `1px solid ${S.borderLight}`,
                    padding: '10px 22px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = S.bg;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#fff';
                  }}
                >
                  {t('screens.portal.cancel')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default MyProfileScreen;
